import { computed } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { useCartStore } from '~/stores/useCartStore'
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore'
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore'

/**
 * Composable for voucher warning logic
 * Handles warning when discount code amount exceeds product price
 *
 * Phase 1: Extracted from VoucherCode.vue to improve separation of concerns
 */
export const useVoucherWarning = () => {
  const cartStore = useCartStore()
  const appliedDiscountCodeStore = useAppliedDiscountCodeStore()
  const chosenVariantDetailsStore = useChosenVariantDetailsStore()

  const { getCart } = storeToRefs(cartStore)
  const { chosenChannel } = storeToRefs(chosenVariantDetailsStore)
  const { totalDiscountCodeDiscount, hasAppliedDiscountCode } = storeToRefs(appliedDiscountCodeStore)

  // Helper: Check if voucher code is valid and applied
  const hasValidVoucherCode = (isApplied) => {
    const discountAmount = totalDiscountCodeDiscount.value || 0
    const hasApplied = hasAppliedDiscountCode.value || isApplied
    return hasApplied && discountAmount > 0
  }

  // Helper: Get price before voucher (base price after product discount)
  const getPriceBeforeVoucher = () => {
    const isCash = chosenChannel.value?.channelKey === 'Cash'
    if (isCash) {
      return chosenChannel.value?.productBundlePrice?.cashPrice || 0
    }
    return chosenChannel.value?.productBundlePrice?.productWithSubscriptionPrice || 0
  }

  // Helper: Get actual price from cart after voucher is applied
  const getActualPriceFromCart = () => {
    const cart = getCart.value
    if (!cart?.cartItemGroupList?.length) {
      return null
    }

    const cartItemGroup = cart.cartItemGroupList[0]
    if (!cartItemGroup?.cartItems?.length) {
      return null
    }

    const deviceItem = cartItemGroup.cartItems.find(item =>
      item.cartProduct?.productTypeKey === 'device' ||
      item.cartProduct?.productTypeKey === 'accessory'
    )

    if (!deviceItem) {
      return null
    }

    return deviceItem.cartProduct?.selectedPrice?.lineItemPrice || null
  }

  // Helper: Calculate price after voucher discount
  const calculatePriceAfterVoucher = (priceBeforeVoucher, discountAmount) => {
    const actualPriceFromCart = getActualPriceFromCart()
    return actualPriceFromCart !== null ? actualPriceFromCart : Math.max(0, priceBeforeVoucher - discountAmount)
  }

  // Helper: Check if discount exceeds price
  const discountExceedsPrice = (priceBeforeVoucher, _discountAmount, priceAfterVoucher) => {
    return priceBeforeVoucher > 0 && priceAfterVoucher <= 0
  }

  // Computed: Show warning if discount exceeds price
  const showDiscountExceedsPriceWarning = (isApplied) => {
    return computed(() => {
      if (!hasValidVoucherCode(isApplied)) {
        return false
      }

      const discountAmount = totalDiscountCodeDiscount.value || 0
      const priceBeforeVoucher = getPriceBeforeVoucher()

      if (priceBeforeVoucher <= 0) {
        return false
      }

      const priceAfterVoucher = calculatePriceAfterVoucher(priceBeforeVoucher, discountAmount)
      return discountExceedsPrice(priceBeforeVoucher, discountAmount, priceAfterVoucher)
    })
  }

  return {
    showDiscountExceedsPriceWarning,
    getPriceBeforeVoucher,
    getActualPriceFromCart,
    calculatePriceAfterVoucher,
    discountExceedsPrice
  }
}
